/**
 * Los campos de la puerta (mision, clave y por que) que piden los 5 sitios del panel que crean tareas
 * (SALV-PUERTA-API-T1). Las reglas viven en application/tareas/puertaTarea.js; aqui solo el marcado y su lectura.
 *
 * Uso:
 *   html: camposPuertaHtml('nx-task', { conMision: true })
 *   vida: const puerta = engancharCamposPuerta(root, 'nx-task', { $titulo, misionFija: null, porQueInicial: '' })
 *         const r = puerta.leer();  // { ok, campos } | { ok:false, campo, error } y senala el campo
 */
import { claveSugerida, normalizarClave, camposPuerta } from '../../application/tareas/puertaTarea.js';
export { claveSugerida };
import { cargarMisionesVivas } from '../../application/tareas/misionesVivas.js';

// Deuda 4: el escape local no cambiaba la comilla simple; ahora es el compartido.
import { esc } from './texto.js';

export function camposPuertaHtml(prefijo, { conMision = true } = {}) {
  return `
    <div data-puerta="${esc(prefijo)}" style="display:flex;gap:6px;flex-wrap:wrap;flex-basis:100%;margin-top:6px;align-items:flex-start">
      ${conMision ? `<select id="${esc(prefijo)}-mision" class="form-input" style="flex:1 1 220px;min-width:0;font-size:0.875rem" aria-label="Mision">
        <option value="">Cargando misiones vivas...</option>
      </select>` : ''}
      <input type="text" id="${esc(prefijo)}-clave" class="form-input" placeholder="CLAVE-DE-FAMILIA" autocomplete="off"
        style="flex:0 1 200px;min-width:0;font-size:0.875rem;font-family:monospace;text-transform:uppercase" aria-label="Clave de la familia">
      <textarea id="${esc(prefijo)}-porque" class="form-input" rows="2" placeholder="Por que es nueva (por que no sirve ninguna tarea que ya existe)"
        style="flex-basis:100%;resize:vertical;font-size:0.875rem" aria-label="Por que es nueva"></textarea>
      <p style="flex-basis:100%;margin:0;font-size:0.875rem;color:#8b949e">La tarea entra por la puerta: va en una mision viva y sale con codigo <code>CLAVE-Tn</code>.</p>
    </div>`;
}

/**
 * @param {ParentNode} root
 * @param {string} prefijo
 * @param {{$titulo?: HTMLInputElement|null, misionFija?: string|null, porQueInicial?: string, claveInicial?: string}} op
 */
export function engancharCamposPuerta(root, prefijo, op = {}) {
  const $ = (s) => root.querySelector('#' + prefijo + '-' + s);
  const $mision = $('mision');
  const $clave = $('clave');
  const $porque = $('porque');
  let claveTocada = false;

  if (op.porQueInicial) $porque.value = op.porQueInicial;
  if (op.claveInicial) { $clave.value = normalizarClave(op.claveInicial); claveTocada = true; }
  const sugerir = () => { if (!claveTocada && op.$titulo) $clave.value = claveSugerida(op.$titulo.value); };
  op.$titulo?.addEventListener('input', sugerir);
  sugerir();
  $clave.addEventListener('input', () => { claveTocada = true; });
  $clave.addEventListener('blur', () => { $clave.value = normalizarClave($clave.value); });

  if ($mision) {
    cargarMisionesVivas().then((r) => {
      if (!$mision.isConnected) return;
      $mision.innerHTML = r.ok
        ? `<option value="">Mision * (${r.misiones.length} vivas)</option>` +
          r.misiones.map((m) => `<option value="${esc(m.code)}">${esc(m.code)}${m.title ? ' — ' + esc(m.title.slice(0, 60)) : ''}</option>`).join('')
        : `<option value="">No se pudo leer las misiones: ${esc(r.error)}</option>`;
    });
  }

  const marcar = (campo) => {
    [$mision, $clave, $porque].forEach((el) => el?.classList.remove('input-error'));
    const el = { mision: $mision, clave: $clave, porque: $porque }[campo];
    if (el) { el.classList.add('input-error'); el.focus(); }
  };

  return {
    leer() {
      const r = camposPuerta({ missionCode: op.misionFija || $mision?.value, clave: $clave.value, porQue: $porque.value });
      if (!r.ok) marcar(r.campo); else marcar('');
      return r;
    }
  };
}

/**
 * Un dialogo propio (nunca prompt/confirm nativos) que pide mision, clave y por que antes de crear.
 * Resuelve con los campos de la puerta, o null si la persona cancela.
 * @param {{titulo: string, explicacion?: string, claveInicial?: string, porQueInicial?: string, misionFija?: string|null, boton?: string}} op
 */
export function pedirPuertaEnDialogo(op) {
  return new Promise((resolve) => {
    document.getElementById('nx-puerta-dialogo')?.remove();
    const capa = document.createElement('div');
    capa.id = 'nx-puerta-dialogo';
    capa.setAttribute('role', 'dialog');
    capa.setAttribute('aria-modal', 'true');
    capa.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,0.6);display:flex;align-items:center;justify-content:center;padding:16px';
    capa.innerHTML = `
      <form data-puerta-dialogo style="background:#161b22;border:1px solid #30363d;border-radius:10px;max-width:480px;width:100%;padding:18px;max-height:90vh;overflow-y:auto">
        <h3 style="margin:0 0 4px;font-size:0.95rem">${esc(op.titulo)}</h3>
        ${op.explicacion ? `<p style="margin:0 0 8px;font-size:0.875rem;color:#8b949e">${esc(op.explicacion)}</p>` : ''}
        ${camposPuertaHtml('nx-puerta-dlg', { conMision: !op.misionFija })}
        <p data-puerta-error style="color:#f85149;font-size:0.875rem;min-height:16px;margin:8px 0 0"></p>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:8px">
          <button type="button" class="btn-ghost btn-sm" data-puerta-cancelar>Cancelar</button>
          <button type="submit" class="btn-primary btn-sm">${esc(op.boton || 'Crear')}</button>
        </div>
      </form>`;
    document.body.appendChild(capa);
    const $form = capa.querySelector('[data-puerta-dialogo]');
    const puerta = engancharCamposPuerta($form, 'nx-puerta-dlg', {
      misionFija: op.misionFija || null, claveInicial: op.claveInicial || '', porQueInicial: op.porQueInicial || ''
    });
    const cerrar = (valor) => { capa.remove(); resolve(valor); };
    capa.querySelector('[data-puerta-cancelar]').addEventListener('click', () => cerrar(null));
    capa.addEventListener('keydown', (e) => { if (e.key === 'Escape') cerrar(null); });
    $form.addEventListener('submit', (e) => {
      e.preventDefault();
      const r = puerta.leer();
      if (!r.ok) { capa.querySelector('[data-puerta-error]').textContent = r.error; return; }
      cerrar(r.campos);
    });
    capa.querySelector('#nx-puerta-dlg-clave')?.focus();
  });
}
